import type { Child, Document, Reference } from '../api/authoring'
import { childByLinkId, isLinked, type LinkPath } from './link'

// Where a command goes.
//
// This is the one piece of routing the client cannot get wrong quietly:
// address the wrong aggregate and the intent still applies, to the wrong
// component, and the author sees an edit land somewhere they were not
// looking. So it is decided in one place, from one rule.
//
// The rule is the model's, not a convenience: **a link path may only
// descend through inline children.** An inline child has no identity and
// no stream — its fields live in its host's document — so editing one is
// a command against the host, carrying the path. A linked child's
// content lives in its own stream, so editing *it* is a command against
// that component, with no path at all.

/** A command's address: which component, and which node inside it. */
export interface CommandTarget {
  /** The component the command is sent to. */
  id: string
  /** The link path within that component. Empty addresses its own document. */
  path: LinkPath
  /**
   * True when the path crossed a link, so the command leaves the
   * document the author is looking at. Screens use it to say so.
   */
  crossedLink: boolean
}

/**
 * Resolve where an intent editing the selected node's **own fields**
 * must be sent.
 *
 * Walking stops at the first linked child, because the path cannot
 * continue past one. A selection deeper than that is not addressable and
 * returns undefined rather than being silently truncated to its nearest
 * addressable ancestor — truncating would command a real node that is
 * not the one selected, which is exactly the failure this function
 * exists to prevent.
 */
export function fieldTarget(root: Document, path: LinkPath): CommandTarget | undefined {
  let children: Child[] | undefined = root.children
  const walked: string[] = []

  for (let index = 0; index < path.length; index += 1) {
    const child = childByLinkId(children, path[index])
    if (!child) return undefined

    if (isLinked(child)) {
      // The path ends here by the model's rule. Anything after it
      // addresses a node in another stream, which this path cannot name.
      if (index !== path.length - 1) return undefined
      return { id: child.id, path: [], crossedLink: true }
    }

    walked.push(child.linkId)
    children = child.children
  }

  return { id: root.id, path: walked, crossedLink: false }
}

/**
 * Resolve where a **structural** intent about the selected placement must
 * be sent — removing it, reordering its siblings, pinning it.
 *
 * Deliberately different from `fieldTarget`, and the difference is the
 * whole reason both exist. A placement is arranged by the node that
 * *holds* it, so `page.block_removed` for a linked Content goes to the
 * **page**, naming a link id — not to the Content, which knows nothing
 * about where it is placed. Sending it to the target would be a command
 * that cannot apply.
 */
export function placementTarget(root: Document, path: LinkPath): CommandTarget | undefined {
  if (path.length === 0) return undefined
  const holder = fieldTarget(root, path.slice(0, -1))
  if (!holder || holder.crossedLink) {
    // The holder itself is reached through a link, so the placement is
    // arranged inside another component's document. Addressable, but the
    // command belongs to that component and the caller needs to have
    // loaded it.
    return holder ? { ...holder, crossedLink: true } : undefined
  }
  return holder
}

/** The link id of the selected placement — what a structural intent names. */
export function placementLinkId(path: LinkPath): string | undefined {
  return path.at(-1)
}

/** A node the selection resolves to: what it holds, and what it refers to. */
export interface SelectedNode {
  type: string
  fields: Record<string, unknown>
  children: Child[]
  /**
   * The node's references, empty where it has none.
   *
   * Empty rather than absent, because the caller draws the edges the
   * *type* declares and reads this for what is on them — a node with no
   * references has every declared edge unfilled, which is a thing to
   * show rather than a case to skip.
   */
  references: Reference[]
}

/**
 * The node a link path resolves to, following links into documents the
 * caller has already loaded.
 *
 * `loaded` is consulted rather than fetched: this is a pure read used
 * while rendering, and a resolver that fetched would turn drawing a tree
 * into a request per node.
 */
export function nodeAt(
  root: Document,
  path: LinkPath,
  loaded?: (id: string) => Document | undefined,
): SelectedNode | undefined {
  if (path.length === 0) {
    return {
      type: root.type,
      fields: (root.fields ?? {}) as Record<string, unknown>,
      children: root.children,
      references: root.references ?? [],
    }
  }

  let children: Child[] | undefined = root.children
  for (let index = 0; index < path.length; index += 1) {
    const child = childByLinkId(children, path[index])
    if (!child) return undefined

    if (isLinked(child)) {
      if (index !== path.length - 1) return undefined
      const target = loaded?.(child.id)
      if (!target) return undefined
      return {
        type: target.type,
        fields: (target.fields ?? {}) as Record<string, unknown>,
        children: target.children,
        references: target.references ?? [],
      }
    }

    if (index === path.length - 1) {
      return {
        type: child.type,
        fields: (child.fields ?? {}) as Record<string, unknown>,
        children: child.children ?? [],
        references: child.references ?? [],
      }
    }
    children = child.children
  }
  return undefined
}
